import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Rocket, Sparkles, Building2, Handshake } from "lucide-react";
import { TID } from "../../lib/testIds";

const SEGMENTS = [
  {
    icon: Rocket,
    tag: "SaaS Companies",
    title: "Next-Layer Growth for Scaling SaaS",
    desc: "Add AI capabilities, modernize architecture, accelerate product delivery, and remove engineering bottlenecks.",
    cta: "Solutions for SaaS Companies",
  },
  {
    icon: Sparkles,
    tag: "Startups & Scaleups",
    title: "Validated Idea to Scalable Product",
    desc: "Move from validated idea to scalable product without creating unnecessary technical complexity.",
    cta: "Solutions for Startups",
  },
  {
    icon: Building2,
    tag: "Enterprises",
    title: "Modernize Without Adding Chaos",
    desc: "Modernize internal systems, automate operations, integrate fragmented workflows, and introduce AI where it creates practical value.",
    cta: "Enterprise Solutions",
  },
  {
    icon: Handshake,
    tag: "Software Agencies",
    title: "Confidential Engineering Partner",
    desc: "Expand delivery capacity with a confidential engineering partner working alongside or behind your brand.",
    cta: "Agency Partnership Program",
  },
];

export const WhoWeHelp = () => {
  return (
    <section
      id="who-we-help"
      data-testid={TID.whoWeHelp}
      className="relative bg-[#050505] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-8 bg-[#FF5F1F]" />
            <span className="font-mono text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#FF5F1F]">
              Built around your growth stage
            </span>
          </div>
          <h2 className="font-headings text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05] text-white">
            Different Businesses. <span className="text-zinc-500">Different Engineering Challenges.</span>
          </h2>
          <p className="mt-6 text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
            A scaling SaaS company does not need the same engagement model as an enterprise
            transformation initiative or a software agency managing delivery capacity. Our
            approach adapts to the commercial and technical context of your business.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {SEGMENTS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={i}
                data-testid={TID.segmentCard(i)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.15 }}
                className="group relative border border-white/10 hover:border-[#FF5F1F]/40 bg-[#0A0A0A] p-8 md:p-10 flex flex-col gap-6 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="w-11 h-11 flex items-center justify-center border border-white/10 group-hover:border-[#FF5F1F] group-hover:text-[#FF5F1F] text-white transition-colors">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#FF5F1F]">
                    {String(i + 1).padStart(2, "0")} / {s.tag}
                  </span>
                </div>
                <h3 className="font-headings text-2xl md:text-3xl font-bold tracking-tight text-white leading-tight">
                  {s.title}
                </h3>
                <p className="text-sm md:text-base text-zinc-400 leading-relaxed">{s.desc}</p>
                <div className="mt-auto pt-6 border-t border-white/5 flex items-center justify-between">
                  <span className="font-mono text-xs uppercase tracking-[0.15em] text-zinc-500 group-hover:text-[#FF5F1F] transition-colors">
                    {s.cta}
                  </span>
                  <ArrowUpRight className="w-4 h-4 text-zinc-600 group-hover:text-[#FF5F1F] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
